import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchOrganizations } from '@api/backoffice';
import type { OrgSortKey } from '@api/backoffice';
import ThSortable from '@components/Backoffice/ThSortable';
import ExportButton from '@components/Backoffice/ExportButton';
import { downloadBackofficeCsv } from '@utils/csvExport';
import {
  PageTitle,
  Card,
  CardHeader,
  Table,
  SearchBar,
  Badge,
  MutedLabel,
  SmallText,
  PaginationBar,
  PaginationInfo,
  PaginationButton,
  PaginationActions,
  EmptyState,
  ActionButton,
} from '@components/Backoffice/BackofficeLayout.styled';
import useSnackbar from '@hooks/useSnackbar';
import type { OrgSummary, PageData } from './types';
import { useBackofficeSort } from './useBackofficeSort';
import CreateOrgDialog from './CreateOrgDialog';

const BackofficeOrganizations: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess } = useSnackbar();
  const [data, setData] = useState<PageData<OrgSummary> | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const { sortKey, sortDirection, toggleSort } = useBackofficeSort<OrgSortKey>('createdAt');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchOrganizations({
        page,
        size: 20,
        search: search || undefined,
        sort: sortKey,
        direction: sortDirection,
      });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [page, search, sortKey, sortDirection]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSort = (key: string) => {
    toggleSort(key as OrgSortKey);
    setPage(0);
  };

  const statusVariant = (status: string) => {
    if (status === 'ACTIVE') return 'success' as const;
    if (status === 'SUSPENDED') return 'error' as const;
    return 'warning' as const;
  };

  const th = (label: string, key: string) => (
    <ThSortable
      label={label}
      sortKey={key}
      activeKey={sortKey}
      direction={sortDirection}
      onToggle={handleSort}
    />
  );

  return (
    <>
      <PageTitle>Organizations</PageTitle>
      <Card>
        <CardHeader>
          <SearchBar
            placeholder="Search by name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <ExportButton
            onExport={() =>
              downloadBackofficeCsv(
                '/api/v1/backoffice/organizations/export',
                { search: search || '', sort: sortKey, direction: sortDirection },
                `organizations_${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
          />
          <ActionButton $variant="primary" onClick={() => setShowCreate(true)}>
            Add organisation
          </ActionButton>
        </CardHeader>
        {loading && !data ? (
          <EmptyState>Loading...</EmptyState>
        ) : data?.content.length === 0 ? (
          <EmptyState>No organizations found</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  {th('Name', 'name')}
                  {th('Status', 'billingStatus')}
                  {th('Plan', 'planType')}
                  {th('Credits / mo', 'monthlyCreditAllowance')}
                  {th('Remaining Credits', 'creditBalance')}
                  {th('Members', 'memberCount')}
                  {th('Created', 'createdAt')}
                </tr>
              </thead>
              <tbody>
                {(data?.content ?? []).map((org) => (
                  <tr
                    key={org.id}
                    onClick={() =>
                      navigate(`/backoffice-admin/organizations/${org.id}`)
                    }
                  >
                    <td>
                      {org.name}
                      {org.personal && (
                        <MutedLabel>personal</MutedLabel>
                      )}
                      <br />
                      <SmallText>{org.id}</SmallText>
                    </td>
                    <td>
                      <Badge $variant={statusVariant(org.billingStatus)}>
                        {org.billingStatus}
                      </Badge>
                    </td>
                    <td>{org.planType || '—'}</td>
                    <td>{org.monthlyCreditAllowance}</td>
                    <td>{org.creditBalance ?? '—'}</td>
                    <td>{org.memberCount}</td>
                    <td>{new Date(org.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {data && data.totalPages > 1 && (
              <PaginationBar>
                <PaginationInfo>
                  Page {data.number + 1} of {data.totalPages} ({data.totalElements} total)
                </PaginationInfo>
                <PaginationActions>
                  <PaginationButton
                    $disabled={page === 0}
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationButton
                    $disabled={page >= data.totalPages - 1}
                    disabled={page >= data.totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </PaginationButton>
                </PaginationActions>
              </PaginationBar>
            )}
          </>
        )}
      </Card>
      {showCreate && (
        <CreateOrgDialog
          onCreated={(organization) =>
            showSuccess(`Organisation ${organization.name} created`)
          }
          onClose={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
    </>
  );
};

export default BackofficeOrganizations;
